import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDateTime } from '../utils/dateFormat';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Activity,
  Search,
  Download,
  Settings,
  Loader2,
  RefreshCw,
  Filter,
  Calendar,
  User,
  Shield,
  Server,
  AlertTriangle,
  Info,
  AlertCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Trash2,
  FileText,
  Eye,
  Clock
} from 'lucide-react';

const LEVEL_CONFIG = {
  debug: { color: 'bg-gray-100 text-gray-800', icon: Info },
  info: { color: 'bg-blue-100 text-blue-800', icon: Info },
  warning: { color: 'bg-amber-100 text-amber-800', icon: AlertTriangle },
  error: { color: 'bg-red-100 text-red-800', icon: AlertCircle },
  critical: { color: 'bg-red-200 text-red-900', icon: XCircle }
};

const CATEGORY_CONFIG = {
  user_action: { color: 'bg-emerald-100 text-emerald-800', icon: User, label: 'User Action' },
  admin_action: { color: 'bg-purple-100 text-purple-800', icon: Shield, label: 'Admin Action' },
  system_event: { color: 'bg-slate-100 text-slate-800', icon: Server, label: 'System Event' }
};

export default function AdminActivityLogs() {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [logsSettings, setLogsSettings] = useState({});
  
  // Filters
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Dialog states
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showLogDetail, setShowLogDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  
  const pageSize = 50;
  
  useEffect(() => {
    fetchLogs();
  }, [page, categoryFilter, eventTypeFilter, levelFilter, userIdFilter, startDate, endDate]);
  
  useEffect(() => {
    fetchSettings();
  }, []);
  
  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', pageSize);
      params.append('skip', page * pageSize);
      
      if (categoryFilter && categoryFilter !== 'all') params.append('category', categoryFilter);
      if (eventTypeFilter && eventTypeFilter !== 'all') params.append('eventType', eventTypeFilter);
      if (levelFilter && levelFilter !== 'all') params.append('level', levelFilter);
      if (userIdFilter) params.append('userId', userIdFilter);
      if (searchQuery) params.append('search', searchQuery);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const response = await axios.get(`/api/admin/events/logs?${params.toString()}`);
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
      setStats(response.data.stats || {});
    } catch (error) {
      toast.error('Failed to load activity logs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/admin/events/settings');
      setLogsSettings(response.data.settings || {});
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };
  
  const updateSettings = async (updates) => {
    setSaving(true);
    try {
      const response = await axios.put('/api/admin/events/settings', updates);
      setLogsSettings(response.data.settings);
      toast.success('Settings updated');
      setShowSettingsDialog(false);
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSearch = () => {
    setPage(0);
    fetchLogs();
  };
  
  const exportLogs = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      if (categoryFilter && categoryFilter !== 'all') params.append('category', categoryFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      if (format === 'csv') {
        const response = await axios.get(`/api/admin/events/logs/export?${params.toString()}`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'activity_logs_export.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const response = await axios.get(`/api/admin/events/logs/export?${params.toString()}`);
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'activity_logs_export.json');
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export logs');
    } finally {
      setExporting(false);
    }
  };
  
  const runCleanup = async () => {
    if (!window.confirm('This will delete logs older than the retention period. Continue?')) {
      return;
    }
    
    setCleaningUp(true);
    try {
      const response = await axios.post('/api/admin/events/cleanup');
      toast.success(response.data.message);
      fetchLogs();
    } catch (error) {
      toast.error('Failed to run cleanup');
    } finally {
      setCleaningUp(false);
    }
  };
  
  const clearFilters = () => {
    setCategoryFilter('all');
    setEventTypeFilter('all');
    setLevelFilter('all');
    setSearchQuery('');
    setUserIdFilter('');
    setStartDate('');
    setEndDate('');
    setPage(0);
  };
  
  const totalPages = Math.ceil(total / pageSize);
  
  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="admin-activity-logs-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div data-testid="admin-activity-logs-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl font-semibold flex items-center gap-2">
            <Activity className="w-6 h-6 text-primary" />
            Activity Logs
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor all system events and user actions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowSettingsDialog(true)}
            data-testid="logging-settings-btn"
          >
            <Settings className="w-4 h-4 mr-1" />
            Settings
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => fetchLogs()}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Events</p>
                <p className="text-2xl font-bold">{stats.total || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-primary opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:border-emerald-300" onClick={() => setCategoryFilter('user_action')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">User Actions</p>
                <p className="text-2xl font-bold">{stats.byCategory?.user_action || 0}</p>
              </div>
              <User className="w-8 h-8 text-emerald-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:border-purple-300" onClick={() => setCategoryFilter('admin_action')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Admin Actions</p>
                <p className="text-2xl font-bold">{stats.byCategory?.admin_action || 0}</p>
              </div>
              <Shield className="w-8 h-8 text-purple-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="cursor-pointer hover:border-red-300" onClick={() => setLevelFilter('error')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Errors</p>
                <p className="text-2xl font-bold text-red-600">{stats.byLevel?.error || 0}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="user_action">User Action</SelectItem>
                <SelectItem value="admin_action">Admin Action</SelectItem>
                <SelectItem value="system_event">System Event</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={eventTypeFilter || "all"} onValueChange={(v) => setEventTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Event Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="page_view">Page View</SelectItem>
                <SelectItem value="assessment">Assessment</SelectItem>
                <SelectItem value="form_submission">Form Submission</SelectItem>
                <SelectItem value="status_change">Status Change</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={levelFilter || "all"} onValueChange={(v) => setLevelFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-[140px]"
                placeholder="Start Date"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-[140px]"
                placeholder="End Date"
              />
            </div>
            
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Input
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
              />
              <Button onClick={handleSearch} variant="outline" size="icon">
                <Search className="w-4 h-4" />
              </Button>
            </div>
            
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Export & Actions */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          Showing {logs.length} of {total} events
        </p>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => exportLogs('csv')}
            disabled={exporting}
          >
            <Download className="w-4 h-4 mr-1" />
            CSV
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => exportLogs('json')}
            disabled={exporting}
          >
            <Download className="w-4 h-4 mr-1" />
            JSON
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={runCleanup}
            disabled={cleaningUp}
            className="text-destructive"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Cleanup Old Logs
          </Button>
        </div>
      </div>
      
      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-semibold mb-2">No logs found</h3>
              <p className="text-muted-foreground">
                Try adjusting your filters or check back later
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead className="text-right">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map(log => {
                  const category = CATEGORY_CONFIG[log.category] || CATEGORY_CONFIG.system_event;
                  const level = LEVEL_CONFIG[log.level] || LEVEL_CONFIG.info;
                  const CategoryIcon = category.icon;
                  const LevelIcon = level.icon;
                  
                  return (
                    <TableRow key={log.id} data-testid={`log-row-${log.id}`}>
                      <TableCell className="font-mono text-sm">
                        {formatDateTime(log.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Badge className={category.color}>
                          <CategoryIcon className="w-3 h-3 mr-1" />
                          {category.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{log.action}</span>
                        {log.eventType && (
                          <span className="text-sm text-muted-foreground ml-2">
                            ({log.eventType})
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {log.userName || log.userEmail || (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={level.color}>
                          <LevelIcon className="w-3 h-3 mr-1" />
                          {log.level}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setShowLogDetail(log)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
      
      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Logging & GDPR Settings</DialogTitle>
            <DialogDescription>
              Configure event logging, retention policies, and auto-triggers
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="logging" className="mt-4">
            <TabsList>
              <TabsTrigger value="logging">Event Logging</TabsTrigger>
              <TabsTrigger value="testimonials">Auto-Triggers</TabsTrigger>
              <TabsTrigger value="gdpr">GDPR & Retention</TabsTrigger>
            </TabsList>
            
            <TabsContent value="logging" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <Label className="font-medium">Log Page Views</Label>
                    <p className="text-sm text-muted-foreground">Track user page navigation</p>
                  </div>
                  <Switch
                    checked={logsSettings.log_page_views}
                    onCheckedChange={(v) => setLogsSettings({...logsSettings, log_page_views: v})}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <Label className="font-medium">Log Assessments</Label>
                    <p className="text-sm text-muted-foreground">Track assessment events</p>
                  </div>
                  <Switch
                    checked={logsSettings.log_assessment_events}
                    onCheckedChange={(v) => setLogsSettings({...logsSettings, log_assessment_events: v})}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <Label className="font-medium">Log Form Submissions</Label>
                    <p className="text-sm text-muted-foreground">Track form submissions</p>
                  </div>
                  <Switch
                    checked={logsSettings.log_form_submissions}
                    onCheckedChange={(v) => setLogsSettings({...logsSettings, log_form_submissions: v})}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <Label className="font-medium">Log Admin Actions</Label>
                    <p className="text-sm text-muted-foreground">Track admin operations</p>
                  </div>
                  <Switch
                    checked={logsSettings.log_admin_actions}
                    onCheckedChange={(v) => setLogsSettings({...logsSettings, log_admin_actions: v})}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <Label className="font-medium">Log System Events</Label>
                    <p className="text-sm text-muted-foreground">Track emails, background jobs</p>
                  </div>
                  <Switch
                    checked={logsSettings.log_system_events}
                    onCheckedChange={(v) => setLogsSettings({...logsSettings, log_system_events: v})}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <Label className="font-medium">Log Errors</Label>
                    <p className="text-sm text-muted-foreground">Always recommended</p>
                  </div>
                  <Switch
                    checked={logsSettings.log_errors}
                    onCheckedChange={(v) => setLogsSettings({...logsSettings, log_errors: v})}
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="testimonials" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground mb-4">
                Automatically request testimonials when participants complete certain milestones.
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="font-medium">Both Assessments Completed</Label>
                    <p className="text-sm text-muted-foreground">
                      Trigger when user completes both EDGE and CORE diagnostics
                    </p>
                  </div>
                  <Switch
                    checked={logsSettings.testimonial_auto_trigger_both_assessments}
                    onCheckedChange={(v) => setLogsSettings({...logsSettings, testimonial_auto_trigger_both_assessments: v})}
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="font-medium">Application Accepted + Both Complete</Label>
                    <p className="text-sm text-muted-foreground">
                      Trigger when accepted applicant completes both assessments
                    </p>
                  </div>
                  <Switch
                    checked={logsSettings.testimonial_auto_trigger_accepted_both}
                    onCheckedChange={(v) => setLogsSettings({...logsSettings, testimonial_auto_trigger_accepted_both: v})}
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="font-medium">Cohort Completed</Label>
                    <p className="text-sm text-muted-foreground">
                      Trigger for all members when cohort status changes to completed
                    </p>
                  </div>
                  <Switch
                    checked={logsSettings.testimonial_auto_trigger_cohort_completed}
                    onCheckedChange={(v) => setLogsSettings({...logsSettings, testimonial_auto_trigger_cohort_completed: v})}
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="gdpr" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div>
                  <Label className="font-medium mb-2 block">Log Retention Period (days)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={3650}
                    value={logsSettings.retention_days || 365}
                    onChange={(e) => setLogsSettings({...logsSettings, retention_days: parseInt(e.target.value)})}
                    className="w-[200px]"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Logs older than this will be automatically deleted
                  </p>
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Inactive User Auto-Cleanup</h4>
                  
                  <div className="space-y-3">
                    <div>
                      <Label className="font-medium mb-2 block">Delete inactive users after (days)</Label>
                      <Input
                        type="number"
                        min={0}
                        value={logsSettings.gdpr_auto_delete_inactive_days || 0}
                        onChange={(e) => setLogsSettings({...logsSettings, gdpr_auto_delete_inactive_days: parseInt(e.target.value)})}
                        className="w-[200px]"
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        Set to 0 to disable auto-deletion
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <Label className="font-medium">Anonymize Instead of Delete</Label>
                        <p className="text-sm text-muted-foreground">
                          Keep anonymized data for analytics instead of full deletion
                        </p>
                      </div>
                      <Switch
                        checked={logsSettings.gdpr_anonymize_on_delete}
                        onCheckedChange={(v) => setLogsSettings({...logsSettings, gdpr_anonymize_on_delete: v})}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => updateSettings(logsSettings)} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Log Detail Dialog */}
      <Dialog open={!!showLogDetail} onOpenChange={() => setShowLogDetail(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Event Details</DialogTitle>
          </DialogHeader>
          
          {showLogDetail && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground text-sm">Timestamp</Label>
                  <p className="font-mono">{formatDateTime(showLogDetail.createdAt)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">Level</Label>
                  <Badge className={LEVEL_CONFIG[showLogDetail.level]?.color}>
                    {showLogDetail.level}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">Category</Label>
                  <p>{CATEGORY_CONFIG[showLogDetail.category]?.label}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">Event Type</Label>
                  <p>{showLogDetail.eventType || '-'}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-muted-foreground text-sm">Action</Label>
                <p className="font-semibold">{showLogDetail.action}</p>
              </div>
              
              {(showLogDetail.userName || showLogDetail.userEmail) && (
                <div>
                  <Label className="text-muted-foreground text-sm">User</Label>
                  <p>{showLogDetail.userName} ({showLogDetail.userEmail})</p>
                </div>
              )}
              
              {showLogDetail.details && Object.keys(showLogDetail.details).length > 0 && (
                <div>
                  <Label className="text-muted-foreground text-sm">Details</Label>
                  <pre className="mt-2 p-3 bg-muted rounded-lg text-sm overflow-auto max-h-[200px]">
                    {JSON.stringify(showLogDetail.details, null, 2)}
                  </pre>
                </div>
              )}
              
              {showLogDetail.ipAddress && (
                <div>
                  <Label className="text-muted-foreground text-sm">IP Address</Label>
                  <p className="font-mono text-sm">{showLogDetail.ipAddress}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogDetail(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
